<section>
  <header>
    <h1 auto="plan-step-1-title">Planning Phase</h1>
  </header>
  <div class="information-container">
    <div>
      <p><strong>Create Objectives and Critical Initiatives</strong></p>
      <p>
        In the Planning Phase you will work toward the development of your institution’s long- and short-term continuous improvement plan. Use the institution’s Priority Statements and Strategic Themes to develop high level Objectives that answer the question, “How will the institution address this priority?” To achieve your institution’s Objectives, you will need to identify the Critical Initiatives that explain the approach you will take to complete the work over a shorter period of time (typically one to two years).
      </p>
      <p>
        Consider asking a question such as, “What Influential Factors could impact the achievement or progress of each Critical Initiative?” This can assist the team in developing an awareness of factors outside its control. Although this work is not represented, determining these Influential Factors can lead to a more accurate evaluation of the institution’s continuous improvement planning efforts.
      </p>
    </div>
    <div class="explanation-margin">
      <div class="download-guide-container">
        <img class="book-icon pointer" src="assets/images/download-guide-icon.svg" alt="book-icon" (click)="openGuide()">
        <span class="download-tagline pointer" (click)="openGuide()">Download the guide</span> to learn more about <strong>Create Objectives and Critical Initiatives</strong>.
      </div>
    </div>
  </div>
  <div *ngIf="planHasPriorities; else hasNoPriorities">
    <ng-container *ngTemplateOutlet="hasPriorities"></ng-container>
  </div>
  <!-- Templates -->
  <ng-template #hasNoPriorities>
    <div class="empty-message">
      <h1>
        <span class="text">To begin this subphase, complete the Envisioning phase first.</span>
      </h1>
    </div>
  </ng-template>
  <ng-template #hasPriorities>
    <div class="add-priority-row">
      <button auto="add-objectives-btn" class="btn btn-primary" (click)="onClickAddObjectives()"
              *ngIf="!isAddObjectivesDisabled">
        Add Objective
      </button>
    </div>
    <ul class="ci-list" *ngIf="hasPrioritiesWithObjectives">
      <li *ngFor="let priority of prioritiesWithObjectives">
        <h2 [style.background-color]="priority.colorCode">{{priority.title}}</h2>
        <!-- objectives container -->
        <ng-container *ngFor="let objective of priority.objectives; let objectiveIndex = index">
          <div class="objective-container"
               [ngClass]="{ 'nested-objective' : objectiveIsNested(objectiveIndex, priority.objectives)} "
               [style.border-left-color]="priority.colorCode">
            <div class="objective-text">
              <h4>Objective</h4>
              <p>{{objective.text}}</p>
            </div>
            <div class="objective-edit-container" *ngIf="!isEditItemDisabled && !isDeleteItemDisabled">
              <button auto="edit-objective-btn"
                      class="fa fa-pencil pointer" pTooltip="Edit Item" tooltipStyleClass="strategies-tooltip"
                      *ngIf="!isEditItemDisabled"
                      (click)="onClickEditObjective(objective)">
              </button>
              <button auto="delete-objective-btn" class="fa fa-trash pointer" pTooltip="Delete Item"
                      tooltipStyleClass="strategies-tooltip"
                      *ngIf="!isDeleteItemDisabled"
                      (click)="onClickDeleteObjectiveItem(objective)">
              </button>
              <button auto="add-critical-initiative-btn" class="btn btn-primary add"
                      (click)="onClickAddInitiative(objective)"
                      [disabled]="isAddInitiativesDisabled(objective)">
                Add Critical Initiative
              </button>
            </div>
          </div>
          <!-- initiatives container -->
          <div  *ngIf="objective.initiatives"
                [ngClass]="{'initiative-table-container': true,
                'reordering': (reorderInProgress$ | async) && reorderParentId === objective.objectiveId}">
            <p-table auto="initiatives-table" styleClass="initiatives-table" [value]="objective.initiatives" (onRowReorder)="onRowReorder(objective, $event)">
              <ng-template pTemplate="body" let-initiative let-index="rowIndex">
                <ng-container *ngIf="!isReorderDisable && !shouldKillHover(initiative)">
                  <ng-container *ngTemplateOutlet="reorderable"></ng-container>
                </ng-container>
                <ng-container *ngIf="isReorderDisable || shouldKillHover(initiative)" >
                  <ng-container *ngTemplateOutlet="notReorderable"></ng-container>
                </ng-container>
                <ng-template #reorderable>
                  <tr [pReorderableRow]="index" class="reorder-initiative-row"
                      [ngClass]="{'kill-hover': shouldKillHover(initiative)}">
                    <td class='initiative-text draggable-cursor reorder-initiative-hover'
                        [ngClass]="{ 'nested-initiative' : initiativeIsNested(rowIndex, objectiveIndex, objective, priority) }"
                        pReorderableRowHandle>
                      <div class="reorder-resize-target">
                        <div class="initiative-top-content-container">
                          <!-- Initiative Action Buttons -->
                          <div class="initiative-edit initiative-action-buttons">
                            <button auto="add-citation-btn"
                                    class="fa fa-plus pointer"
                                    pTooltip="Add Citation"
                                    tooltipPosition="bottom"
                                    [disabled]="isCitationAddDisabled(initiative)"
                                    *ngIf="!isEditItemDisabled"
                                    (click)="onAddCitation(initiative, objective)">
                            </button>
                            <button auto="edit-critical-initiative-btn"
                                    class="fa fa-pencil pointer"
                                    pTooltip="Edit Item"
                                    tooltipPosition="bottom"
                                    *ngIf="!isEditItemDisabled"
                                    (click)="onClickEditInitiative(initiative, objective)">
                            </button>
                            <button auto="delete-critical-initiative-btn"
                                    class="fa fa-trash pointer delete-critical-initiative-btn"
                                    pTooltip="Delete Item"
                                    tooltipPosition="bottom"
                                    *ngIf="!isDeleteItemDisabled"
                                    (click)="onClickDeleteInitiativeItem(initiative)">
                            </button>
                          </div>
                          <h4 class="draggable-cursor" pReorderableRowHandle>Critical Initiative</h4>
                          <p class="draggable-cursor hard-break-text" pReorderableRowHandle>{{ initiative.description }}</p>
                        </div>
                        <!-- Citation Edit/add -->
                        <div *ngIf="shouldShowRTE(initiative)" class="edit-citation-container">
                          <app-rich-content editorTitle="Evidence-Based Practice Citation"
                                            placeholderText="Enter an optional evidence-based practice citation here"
                                            [content]="initiative.citation || ''"
                                            [maxContentLength]="200"
                                            [isReadOnly]="isEditItemDisabled"
                                            (contentUpdate)="onCitationChanged($event)"
                                            [autoFocus]="true"
                                            [forceEdit]="true"
                                            [updateTimeOverride]="1"
                          >
                          </app-rich-content>
                          <div class="button-row">
                            <button auto="edit-user-cancle-btn" class="btn btn-alt left-btn" type="button" (click)="onCitationCancel()">Cancel</button>
                            <button auto="edit-user-save-btn" class="btn" type="submit" [disabled]="!lastKnownExpandedLocation.citation || lastKnownExpandedLocation.citation.trim() !== ''" (click)="onCitationSave()">Save</button>
                          </div>
                        </div>
                        <!-- Citation view -->
                        <div *ngIf="shouldShowCitationView(initiative)" class="citation-outer-content">
                          <div class="citation-title"><strong>Evidence-Based Practice Citation</strong></div>
                          <div class="citation-inner-content">
                            <div class="citation-text" ><p [innerHTML]="sanitize(initiative.citation)"></p></div>
                            <div class="citation-button-container">
                              <button auto="edit-critical-initiative-btn" class="fa fa-pencil pointer" pTooltip="Edit Citation"
                                      tooltipStyleClass="strategies-tooltip"
                                      tooltipPosition="bottom"
                                      *ngIf="!isEditItemDisabled"
                                      (click)="editInitiativeCitation(initiative, objective)">
                              </button>
                              <button auto="delete-critical-initiative-btn" class="fa fa-trash pointer" pTooltip="Delete Citation"
                                      tooltipStyleClass="strategies-tooltip"
                                      tooltipPosition="bottom"
                                      *ngIf="!isDeleteItemDisabled"
                                      (click)="onCitationDelete(initiative, objective)">
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- end citation stuff -->
                    </td>
                  </tr>
                </ng-template>
                <ng-template #notReorderable>
                  <tr class="reorder-initiative-row"
                      [ngClass]="{'kill-hover': true}">
                    <td class='initiative-text reorder-initiative-hover'
                        [ngClass]="{ 'nested-initiative' : initiativeIsNested(rowIndex, objectiveIndex, objective, priority) }"
                        >
                      <div class="reorder-resize-target">
                        <div class="initiative-top-content-container">
                          <!-- Initiative Action Buttons -->
                          <div class="initiative-edit initiative-action-buttons">
                            <button auto="add-citation-btn"
                                    class="fa fa-plus pointer"
                                    pTooltip="Add Citation"
                                    tooltipPosition="bottom"
                                    [disabled]="isCitationAddDisabled(initiative)"
                                    *ngIf="!isEditItemDisabled"
                                    (click)="onAddCitation(initiative, objective)">
                            </button>
                            <button auto="edit-critical-initiative-btn"
                                    class="fa fa-pencil pointer"
                                    pTooltip="Edit Item"
                                    tooltipPosition="bottom"
                                    *ngIf="!isEditItemDisabled"
                                    (click)="onClickEditInitiative(initiative, objective)">
                            </button>
                            <button auto="delete-critical-initiative-btn"
                                    class="fa fa-trash pointer delete-critical-initiative-btn"
                                    pTooltip="Delete Item"
                                    tooltipPosition="bottom"
                                    *ngIf="!isDeleteItemDisabled"
                                    (click)="onClickDeleteInitiativeItem(initiative)">
                            </button>
                          </div>
                          <h4>Critical Initiative</h4>
                          <p>{{ initiative.description }}</p>
                        </div>
                        <!-- Citation Edit/add -->
                        <div *ngIf="shouldShowRTE(initiative)" class="edit-citation-container">
                          <app-rich-content editorTitle="Evidence-Based Practice Citation"
                                            placeholderText="Enter an optional evidence-based practice citation here"
                                            [content]="initiative.citation || ''"
                                            [maxContentLength]="200"
                                            [isReadOnly]="isEditItemDisabled"
                                            (contentUpdate)="onCitationChanged($event)"
                                            [autoFocus]="true"
                                            [forceEdit]="true"
                                            [updateTimeOverride]="10"
                          >
                          </app-rich-content>
                          <div class="button-row">
                            <button auto="edit-user-cancle-btn" class="btn btn-alt left-btn" type="button" (click)="onCitationCancel()">Cancel</button>
                            <button auto="edit-user-save-btn" class="btn" type="submit" [disabled]="!lastKnownExpandedLocation.citation || lastKnownExpandedLocation.citation.trim() === ''" (click)="onCitationSave()">Save</button>
                          </div>
                        </div>
                        <!-- Citation view -->
                        <div *ngIf="shouldShowCitationView(initiative)" class="citation-outer-content">
                          <div class="citation-title"><strong>Evidence-Based Practice Citation</strong></div>
                          <div class="citation-inner-content">
                            <div class="citation-text" ><p [innerHTML]="sanitize(initiative.citation)"></p></div>
                            <div class="citation-button-container">
                              <button auto="edit-critical-initiative-btn" class="fa fa-pencil pointer" pTooltip="Edit Citation"
                                      tooltipStyleClass="strategies-tooltip"
                                      tooltipPosition="bottom"
                                      *ngIf="!isEditItemDisabled"
                                      (click)="editInitiativeCitation(initiative, objective)">
                              </button>
                              <button auto="delete-critical-initiative-btn" class="fa fa-trash pointer" pTooltip="Delete Citation"
                                      tooltipStyleClass="strategies-tooltip"
                                      tooltipPosition="bottom"
                                      *ngIf="!isDeleteItemDisabled"
                                      (click)="onCitationDelete(initiative, objective)">
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- end citation stuff -->
                    </td>
                  </tr>
                </ng-template>
              </ng-template>
            </p-table>
            <div *ngIf="(reorderInProgress$ | async) && reorderParentId === objective.objectiveId" class="reordering-msg-container">
              <div class="reordering-spinner">
                <i class="fa fa-spinner fa-spin"></i>
              </div>
              <div *ngIf="!(reorderFailed$ | async)" class="reordering-message">Please Wait</div>
              <div *ngIf="(reorderFailed$ | async)" class="reordering-message refresh-pending"><div>Another user wth permissions to this plan has changed this information.</div><br/><div>Please wait...</div></div>
            </div>
          </div><!-- end of initiatives container -->
        </ng-container> <!-- end of objectives container-->
      </li>
    </ul>
  </ng-template>
  <ng-template #loading>
    <div class="loading-panel">
      <div class='loading-spinner'>
        <i class="fa fa-spinner fa-spin"></i>
      </div>
    </div>
  </ng-template>
</section>

<p-sidebar #objectiveSidebar
           [visible]="(objectiveComp.isActive$ | async)"
           appendTo="body"
           position="right"
           styleClass="priorities-sidebar"
           (onShow)="objectiveComp.onShowSidebar(objectiveSidebar)"
           (onHide)="objectiveComp.onHideSidebar()">
  <app-objective #objectiveComp
                 (create)="onObjectiveCreate($event)"
                 (createError)="onItemError($event)"
                 (edit)="onObjectiveEdit($event)"
                 (editError)="onItemError($event)">
  </app-objective>
</p-sidebar>

<p-dialog #confirmDeleteObjectiveDialog styleClass="strategies-confirm-dialog" [modal]="true" [closable]="false"
          appendTo="body">
  <p-header>
    <img src="assets/images/exclamation.svg" alt="Alert">
    <span class="dialog-title">Delete Objective</span>
  </p-header>
  <div>Deleting this will also impact any associated content.</div>
  <div><strong>Are you sure?</strong></div>
  <p-footer>
    <button auto="planning-objective-cancel-btn" class="btn btn-alt" type="button" pButton label="Cancel"
            (click)="confirmDeleteObjectiveDialog.visible = false">
    </button>
    <button auto="planning-objective-delete-btn" class="btn" type="button" pButton label="Delete"
            (click)="onConfirmObjectiveDeleteDialogAccept()"></button>
  </p-footer>
</p-dialog>

<p-sidebar #initiativeSidebar
           [(visible)]="isInitiativeActive"
           appendTo="body"
           position="right"
           styleClass="priorities-sidebar"
           (onShow)="initiativeComp.onShowSidebar(initiativeSidebar)"
           (onHide)="initiativeComp.onHideSidebar()">
  <app-initiative #initiativeComp
                  (create)="onInitiativeCreate($event)"
                  (edit)="onInitiativeEdit($event)"
                  (createError)="onItemError($event)"
                  (editError)="onItemError($event)"
                  (cancel)="isInitiativeActive = false"
                  [strategyId]="strategyId">
  </app-initiative>
</p-sidebar>

<p-dialog #confirmDeleteInitiativeDialog styleClass="strategies-confirm-dialog" [modal]="true" [closable]="false"
          appendTo="body">
  <p-header>
    <img src="assets/images/exclamation.svg" alt="Alert">
    <span class="dialog-title">Delete Critical Initiative</span>
  </p-header>
  <div>Deleting this will also impact any associated content.</div>
  <div><strong>Are you sure?</strong></div>
  <p-footer>
    <button auto="planning-ci-cancel-btn" class="btn btn-alt" type="button" pButton label="Cancel"
            (click)="confirmDeleteInitiativeDialog.visible = false">
    </button>
    <button auto="planning-ci-delete-btn" class="btn" type="button" pButton label="Delete"
            (click)="onConfirmInitiativeDeleteDialogAccept()"></button>
  </p-footer>
</p-dialog>

<p-dialog #confirmDeleteCitationDialog styleClass="delete-modal"
          [modal]="true"
          [closable]="false"
          [draggable]="false"
          appendTo="body">
  <p-header class="delete-modal-header">
    <img src="assets/images/exclamation-white.svg" alt="Alert">
    <span class="dialog-title">Delete Citation</span>
  </p-header>
  <div><strong>Are you sure you want to delete this citation?</strong></div>
  <div>Once the information is deleted, it can not be undone.</div>
  <p-footer>
    <button auto="cancel-btn" class="btn btn-alt cancel" type="button" pButton label="Cancel"
            (click)="confirmDeleteCitationDialog.visible = false">
    </button>
    <button auto="delete-btn" class="btn delete" type="button" pButton label="Delete"
            (click)="onConfirmCitationDelete()"></button>
  </p-footer>
</p-dialog>
